import { useEffect, useState } from "react";

/**
 * Whether a horizontal scroller has more to show, and which way.
 *
 * The shell's secondary strip runs off the right edge of a 390px screen
 * mid-word, and a word cut in half is not a scroll affordance — it reads as a
 * rendering bug. These flags drive a fade at whichever edge has content beyond
 * it, and only then: a fade on a strip that fits is a lie about there being
 * more. The month grid on the clinic-cost report has exactly the same problem.
 *
 * A callback ref rather than `useRef`, because the strip mounts and unmounts as
 * the viewport crosses `lg` and an effect keyed on a ref object would not
 * re-run when the node appears.
 *
 * The `setEdges` guard is load-bearing. `measure` runs on every scroll frame,
 * and handing `useState` a fresh object each time means `Object.is` never bails
 * out — so dragging the strip one pixel re-rendered the entire app chrome, per
 * frame, on the phone that can least afford it. Returning `prev` unchanged when
 * neither flag moved makes the common case free.
 */
export function useScrollEdges() {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });

  useEffect(() => {
    if (!node) return;
    const measure = () => {
      const furthest = node.scrollWidth - node.clientWidth;
      const atStart = node.scrollLeft <= 1;
      const atEnd = furthest <= 1 || node.scrollLeft >= furthest - 1;
      setEdges((prev) =>
        prev.atStart === atStart && prev.atEnd === atEnd
          ? prev
          : { atStart, atEnd },
      );
    };
    measure();
    node.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      node.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [node]);

  return { setNode, ...edges };
}
